<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
  view: {
    type: String,
    default: 'home',
  },
});

const hasIntroText = computed(
  () => props.config.welcomeHeading || props.config.welcomeTagline
);

const showHeader = computed(() => props.config.showHeader !== false);

const agentName = computed(
  () =>
    props.config.websiteName ||
    props.config.welcomeHeading ||
    t('INBOX_MGMT.WIDGET_BUILDER.BODY.AGENT_NAME')
);
</script>

<template>
  <div
    class="h-[31.25rem] w-80 rounded-lg overflow-hidden shadow-md flex flex-col relative"
    :style="{
      background: 'var(--wt-bg, #292932)',
      backgroundImage: view === 'home' ? 'var(--wt-bg-image, none)' : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }"
  >
    <!-- Gradient overlay matching dialog-ui--gradient -->
    <div
      class="absolute inset-0 pointer-events-none z-0"
      :style="{ backgroundImage: 'var(--wt-gradient)' }"
    />

    <!-- HOME VIEW -->
    <div
      v-if="view === 'home'"
      class="relative z-10 flex flex-col flex-1 overflow-y-auto px-8 py-8 gap-8"
    >
      <!-- ChatHeaderExpanded: rounded-2xl, rgba(0,0,0,.25) tint, avatar border uses banner-bg -->
      <div
        v-if="showHeader && hasIntroText"
        class="flex flex-row p-4 box-border w-full rounded-2xl"
        :style="{ background: 'var(--wt-banner-bg, #7663ab)' }"
      >
        <div class="flex-none">
          <img
            v-if="config.logo"
            class="border-2 border-solid h-8 w-8 rounded-full"
            :style="{ borderColor: 'var(--wt-banner-bg, #7663ab)' }"
            :src="config.logo"
          />
        </div>
        <div class="flex flex-col justify-center ml-4 w-full min-w-0">
          <h2
            v-if="config.welcomeHeading"
            class="text-xl leading-8 font-semibold opacity-75"
            :style="{ color: 'var(--wt-banner-text, #ffffff)' }"
          >
            {{ config.welcomeHeading }}
          </h2>
          <p
            v-if="config.welcomeTagline"
            class="text-base leading-6 font-semibold"
            :style="{ color: 'var(--wt-banner-text, #ffffff)' }"
          >
            {{ config.welcomeTagline }}
          </p>
        </div>
        <div class="flex-none flex items-start ml-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5 opacity-75"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            :style="{ color: 'var(--wt-banner-icon-color, #ffffff)' }"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
      </div>

      <!-- Inline title (when header is hidden) -->
      <div v-if="!showHeader && hasIntroText" class="flex flex-col gap-2">
        <h2
          v-if="config.welcomeHeading"
          class="text-4xl font-semibold text-white"
        >
          {{ config.welcomeHeading }}
        </h2>
        <p v-if="config.welcomeTagline" class="text-2xl text-white opacity-75">
          {{ config.welcomeTagline }}
        </p>
      </div>

      <!-- Hero input (ChatInputWrap variant="hero") -->
      <div class="w-full flex flex-row gap-2 items-end">
        <div
          class="flex-1 flex items-center rounded-2xl shadow-lg py-4 px-6 border border-slate-100"
          :style="{ background: 'var(--wt-input-bg, #ffffff)' }"
        >
          <span
            class="flex-1 text-base select-none"
            :style="{ color: 'var(--wt-input-placeholder, #9ca3af)' }"
          >
            Ask a question...
          </span>
        </div>
        <!-- ChatSendButton: w-12 h-12 rounded-full -->
        <button
          class="flex items-center justify-center rounded-full w-12 h-12 flex-shrink-0"
          :style="{ background: 'var(--wt-primary, #7663ab)' }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 19V5M5 12l7-7 7 7"
            />
          </svg>
        </button>
      </div>

      <!-- ConversationStarters: grid-cols-2, white bordered rounded-xl cards -->
      <div class="grid grid-cols-2 gap-3 w-full">
        <div
          v-for="(label, i) in [
            'How does this work?',
            'Get started',
            'View pricing',
            'Talk to support',
          ]"
          :key="label"
          class="rounded-xl p-4 border border-slate-100 shadow-sm flex flex-row justify-between items-center gap-2 select-none"
          :style="{
            opacity: i >= 2 ? '0.5' : '1',
            background: 'var(--wt-input-bg, #ffffff)',
          }"
        >
          <span
            class="text-sm font-medium truncate"
            :style="{ color: 'var(--wt-input-text, #3c4858)' }"
            >{{ label }}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-3.5 h-3.5 flex-shrink-0 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </div>

    <!-- CONVERSATION VIEW -->
    <div v-else class="relative z-10 flex flex-col flex-1 overflow-hidden">
      <!-- ChatHeader: expanded header to match ChatHeaderExpanded.vue -->
      <div v-if="showHeader" class="px-8 pt-8 flex-shrink-0">
        <div
          class="flex flex-row p-4 box-border w-full rounded-2xl"
          :style="{ background: 'var(--wt-banner-bg, #7663ab)' }"
        >
          <div class="flex-none">
            <img
              v-if="config.logo"
              class="border-2 border-solid h-8 w-8 rounded-full"
              :style="{ borderColor: 'var(--wt-banner-bg, #7663ab)' }"
              :src="config.logo"
            />
            <div
              v-else
              class="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold border-2 border-solid"
              :style="{
                borderColor: 'var(--wt-banner-bg, #7663ab)',
                background: 'rgba(255,255,255,0.15)',
              }"
            >
              A
            </div>
          </div>
          <div class="flex flex-col justify-center ml-4 w-full min-w-0">
            <h2
              class="text-xl leading-8 font-semibold opacity-75"
              :style="{ color: 'var(--wt-banner-text, #ffffff)' }"
            >
              {{ config.welcomeHeading || config.websiteName || 'Support' }}
            </h2>
            <p
              class="text-base leading-6 font-semibold"
              :style="{ color: 'var(--wt-banner-text, #ffffff)' }"
            >
              {{ config.welcomeTagline || 'Online' }}
            </p>
          </div>
          <div class="flex-none flex items-start ml-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5 opacity-75"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              :style="{ color: 'var(--wt-banner-icon-color, #ffffff)' }"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Messages area -->
      <div class="flex flex-col flex-1 overflow-y-auto px-6 py-4 gap-3">
        <!-- Agent message -->
        <div class="flex flex-col">
          <p
            class="text-xs font-medium mb-1 pl-0.5"
            :style="{ color: 'var(--wt-muted-text, #3c4858)' }"
          >
            {{ agentName }}
          </p>
          <div class="flex items-end">
            <div
              class="max-w-[72%] rounded-2xl rounded-bl-sm px-3 py-2 text-xs leading-relaxed"
              style="
                background: transparent;
                color: var(--wt-agent-bubble-text, #ffffff);
              "
            >
              👋 Hi there! How can I help you today?
            </div>
          </div>
        </div>
        <!-- User message -->
        <div class="flex items-end justify-end">
          <div
            class="max-w-[72%] rounded-2xl rounded-br-sm px-3 py-2 text-xs leading-relaxed"
            :style="{
              background: 'var(--wt-user-bubble-bg, #7663ab)',
              color: 'var(--wt-user-bubble-text, #ffffff)',
            }"
          >
            How does pricing work?
          </div>
        </div>
        <!-- Agent message -->
        <div class="flex flex-col">
          <p
            class="text-xs font-medium mb-1 pl-0.5"
            :style="{ color: 'var(--wt-muted-text, #3c4858)' }"
          >
            {{ agentName }}
          </p>
          <div class="flex items-end">
            <div
              class="max-w-[72%] rounded-2xl rounded-bl-sm px-3 py-2 text-xs leading-relaxed"
              style="
                background: transparent;
                color: var(--wt-agent-bubble-text, #ffffff);
              "
            >
              We offer flexible monthly and annual plans. Would you like me to
              walk you through them?
            </div>
          </div>
        </div>
      </div>

      <!-- Input bar (ChatInputWrap standard + ChatSendButton) -->
      <div
        class="px-6 py-6 flex flex-row items-center gap-1"
        :style="{ background: 'var(--wt-bg, #292932)' }"
      >
        <div
          class="flex-1 flex items-center rounded-[32px] py-2 px-6 transition-all duration-200"
          :style="{ background: 'var(--wt-input-bg, #ffffff)' }"
        >
          <span
            class="text-xs select-none"
            :style="{ color: 'var(--wt-input-placeholder, #9ca3af)' }"
            >Reply...</span>
          <div
            class="ml-auto flex items-center"
            :style="{ color: 'var(--wt-input-placeholder, #9ca3af)' }"
          >
            <!-- Attachment Icon -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
            <!-- Emoji Icon -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
        <!-- ChatSendButton: w-12 h-12 rounded-full -->
        <button
          class="flex items-center justify-center rounded-full w-12 h-12 ml-1 flex-shrink-0"
          :style="{ background: 'var(--wt-primary, #7663ab)' }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 19V5M5 12l7-7 7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>
